<template>
    <div class="card">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">Order Payments</h5>
            <b-dropdown variant="outline-dark" size="sm" split right v-on:click="showModal(false)">
                <template v-slot:button-content>
                    <i class="fas fa-plus mr-1"></i>Add
                </template>
                <b-dropdown-item v-on:click="showModal(false)">Payment Request</b-dropdown-item>
                <b-dropdown-item v-on:click="showModal(true)">Received Payment</b-dropdown-item>
            </b-dropdown>
        </div>

        <b-table striped hover fixed small :items="currentOrderPayments" :fields="orderPaymentFields" class="mb-0">
            <template #cell(action)="row">                
                <b-button size="sm" v-if="row.item.new" style="font-size: 10px;" @click="removePaymentFromList(row.index)" class="p1" variant="danger">
                    Remove
                </b-button>
            </template>
        </b-table>

        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     v-model="dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(newOrderPayment)">

                <template #modal-header>
                    <h5 class="modal-title">{{ currentOrderPayment.isReceivedPayment ? 'New Received Payment' : 'New Payment Request' }}</h5>
                </template>

                <template #default>
                    <b-alert :show="(currentErrors != null) && (currentErrors.length > 0)" variant="danger" dismissible>
                        <div v-for="(error, index) in currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>

                    <b-form-row>

                        <validation-provider :rules="{ max:30 }"
                                             :custom-messages="customMessages.description"
                                             name="Amount" v-slot="vp" slim>
                            <b-form-group label="Description"
                                          label-for="description"
                                          class="col-12">
                                <b-form-input name="description"
                                              v-model="currentOrderPayment.description"></b-form-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>

                            </b-form-group>
                        </validation-provider>

                        <validation-provider :rules="{ required: true, min_value: { min: 0.01 } }"
                                             :custom-messages="customMessages.amount"
                                             name="Amount" v-slot="vp" slim>
                            <b-form-group label="Amount"
                                          label-for="amount"
                                          class="col-md-6">
                                <currency-input v-model="currentOrderPayment.amount"
                                                :locale="$config.currentLocale"
                                                :currency="$config.defaultCurrencyCode"
                                                :precision="2"
                                                :class="vp.errors[0] ? 'text-right form-control form-control is-invalid' : 'text-right form-control form-control'">
                                </currency-input>
                                <b-form-checkbox name="amountIncludesGst"
                                                 v-if="gstPercentage != 0"
                                                 v-model="currentOrderPayment.amountIncludesGst"> Amount includes {{ $t("Shared.Label.GST") }}</b-form-checkbox>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <b-form-group label="Due Date"
                                      label-for="dueDateTimeUTC"
                                      class="col-md-6"
                                      v-if="!currentOrderPayment.isReceivedPayment">
                            <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                               :min="minDueDate"
                                               placeholder="undefined"
                                               locale="en-AU" right reset-button
                                               v-model="currentOrderPayment.dueDateTimeUTC">
                            </b-form-datepicker>
                        </b-form-group>
                        <b-form-group label="Received Date"
                                      label-for="receivedDateTimeUTC"
                                      class="col-md-6"
                                      v-else>
                            <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                               :min="minReceivedDate"
                                               :max="maxReceivedDate"
                                               placeholder="undefined"
                                               locale="en-AU" right reset-button
                                               v-model="currentOrderPayment.receivedDateTimeUTC">
                            </b-form-datepicker>
                        </b-form-group>
                    </b-form-row>
                </template>

                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="ok()">Create</button>
                </template>
            </b-modal>
        </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import axios from 'axios';
    import BootstrapVue from 'bootstrap-vue';
    import VueCurrencyInput from 'vue-currency-input';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, min_value, max } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('min_value', min_value);
    extend('max', max);
    localize('en', en);

    import ErrorsMixin from '../mixins/ErrorsMixin';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import NumbersMixin from '../mixins/NumbersMixin';
    import DisplayFormatMixin from '../mixins/DisplayFormatMixin';

    Vue.use(BootstrapVue);
    Vue.use(NumbersMixin);
    Vue.use(VueCurrencyInput);

    export default {
        name: 'OrderPayments',
        mixins: [ErrorsMixin, DateTimeMixin, NumbersMixin, DisplayFormatMixin],
        components: {
            'validation-provider': ValidationProvider,
            'validation-observer': ValidationObserver
        },
        model: {
            event: 'change',
            prop: 'initialNewOrderPayments'
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            orderId: {
                type: String,
                default: null
            },
            courtRegistryId: {
                type: String,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                default: null
            },
            effectiveOrderedDate: {
                type: String,
                default: null
            },
            initialNewOrderPayments: {
                type: Array,
                default: null
            }
        },
        data: function () {
            return {
                dialogOpen: false,

                currentOrderPayments: [],
                newOrderPayments: this.initialNewOrderPayments,

                currentOrderPayment: {
                    createdDateTimeUTC: null,
                    description: null,
                    amount: null,
                    amountIncludesGst: true,
                    status: null                   
                },
                 
                customMessages: {
                    amount: {
                        required: 'Amount is required',
                        min_value: 'Amount must be ' + this.formatAsCurrency(0.01, this.$config.defaultCurrencyCode) + ' or more',
                        max_value: 'max value'
                    },
                    description: {
                        max: 'Maximum description length is 30 characters'
                    }
                },

                currentErrors: [],
                orderPaymentFields: [
                    
                    {
                        key: 'createdDateTimeUTC',
                        label: 'Created',
                        class: 'text-left ',
                        formatter: (value) => {
                            return value ? this.toDisplayString(value, { type: "datetime" }) : null;
                        }
                    },
                    {
                        key: 'reference',
                        label: 'Reference',
                        class: ' text-left',
                        formatter: (value) => {
                            return value;
                        }
                    },
                    {
                        key: 'description',
                        label: 'Description',
                        class: ' text-left',
                        formatter: (value) => {
                            return value;
                        }
                    },
                    {
                        key: 'amount',
                        label: 'Amount',
                        class: ' text-right',
                        formatter: (value, key, item) => {
                            if (item.new) {
                                if (item.amountIncludesGst) {
                                    return this.formatAsCurrency(value) + ' (incl. ' + this.$t("Shared.Label.GST") + ')';
                                }
                                return this.formatAsCurrency(value);
                            }
                            return this.formatAsCurrency(value, item.currencyCode);
                        }
                    },
                    {
                        key: 'refundedAmount',
                        label: 'Amount refunded',
                        class: ' text-right',
                        formatter: (value, key, item) => {
                            if (value) {
                                return this.formatAsCurrency(value, item.currencyCode);
                            }
                            return null;
                        }
                    },
                    {
                        key: 'dueDateTimeUTC',
                        label: 'Due',
                        class: ' text-center',
                        formatter: (value) => {
                            return value ? this.toDisplayString(value, { type: "datetime" }) : null;
                        }
                    },
                    {
                        key: 'processedOrReceivedDateTimeUTC',
                        label: 'Processed/Received',
                        class: ' text-center',
                        formatter: (value) => {
                            return value ? this.toDisplayString(value, { type: "datetime", options: { mode: "checktime" } }) : null;
                        }
                    },
                    {
                        key: 'status',
                        label: 'Status',
                        class: 'text-center fit'
                    },
                    {
                        key: 'action',
                        label: '',
                        class: 'text-right'
                    }
                ],

                gstPercentage: 0,
            };
        },
        watch: {
            courtRegistryId: function () {
                this.loadGstValues();
            },
            interviewOrganisationId: function () {
                this.loadGstValues();
            },
        },
        computed: {
            minDueDate() {
                return this.setTimeComponents(this.now(), 0);
            },
            minReceivedDate() {
                var effectiveOrderedDate = this.now();
                if (this.effectiveOrderedDate) effectiveOrderedDate = this.parseDateTime(this.effectiveOrderedDate, 'YYYY-MM-DD', true, true);
                return this.setTimeComponents(effectiveOrderedDate, 0, 0, 0);
            },
            maxReceivedDate() {
                return this.setTimeComponents(this.now(), 23, 59, 59);
            }
        },
        methods: {
            showModal: function (isReceivedPayment) {
                this.currentOrderPayment.isReceivedPayment = isReceivedPayment;
                this.currentOrderPayment.description = null;
                this.currentOrderPayment.dueDateTimeUTC = null;
                this.currentOrderPayment.receivedDateTimeUTC = null;
                this.currentOrderPayment.processedOrReceivedDateTimeUTC = null;
                this.currentOrderPayment.amount = null;
                this.currentOrderPayment.amountIncludesGst = true;
                this.dialogOpen = true;
            },

            loadGstValues: function () {
                var url = '';

                if (this.courtRegistryId != null) {
                    url = this.$urlHelper.getApiUrl('payment', 'gstpercentage/bycourtregistry', [this.courtRegistryId])
                }
                else if (this.interviewOrganisationId != null) {
                    url = this.$urlHelper.getApiUrl('payment', 'gstpercentage/byintervieworganisation', [this.interviewOrganisationId])
                }
                else {
                    url = this.$urlHelper.getApiUrl('payment', 'gstpercentage')
                }

                console.log(url);
                axios
                    .get(url)
                    .then(response => {
                        this.gstPercentage = response.data;
                    })
                    .catch(ex => {
                        console.log(JSON.stringify(ex));
                        this.currentErrors = this.handleAxiosException(ex);
                    });
            },

            loadOrderPayments: function () {
                if (this.orderId) {
                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'payment/byorder/' + this.orderId;
                    console.log(url);
                    axios
                        .get(url)
                        .then(response => {
                            this.currentOrderPayments = response.data;
                        })
                        .catch(ex => {
                            console.log(JSON.stringify(ex));
                            this.currentErrors = this.handleAxiosException(ex);
                        });
                }
            },

            newOrderPayment: function () {
                var newPaymentObject = {};

                if (this.gstPercentage == 0) { this.currentOrderPayment.amountIncludesGst = false; }

                Object.assign(newPaymentObject, this.currentOrderPayment);
                newPaymentObject.new = true;
                newPaymentObject.status = "New";

                if (newPaymentObject.dueDateTimeUTC) {
                    var dueDateTimeUTC = newPaymentObject.dueDateTimeUTC + 'T23:59:59'; //set the expiry time to the end of that day
                    newPaymentObject.dueDateTimeUTC = this.parseDateTime(dueDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', false, true);
                }
                if (newPaymentObject.receivedDateTimeUTC) {
                    var receivedDateTimeUTC = newPaymentObject.receivedDateTimeUTC + 'T00:00:01'; //set the received time to one second past midnight this will then only display the date
                    newPaymentObject.receivedDateTimeUTC = this.parseDateTime(receivedDateTimeUTC, 'YYYY-MM-DDTHH:mm:ss', true, true);
                    newPaymentObject.processedOrReceivedDateTimeUTC = newPaymentObject.receivedDateTimeUTC;
                }

                this.currentOrderPayments.push(newPaymentObject);
                if (this.newOrderPayments == null) this.newOrderPayments = [];
                this.newOrderPayments.push(newPaymentObject);
                this.$emit('change', this.newOrderPayments);
                this.dialogOpen = false;
            },

            removePaymentFromList: function (index) {
                if (this.newOrderPayments != null) {
                    var existingOrdersCount = this.currentOrderPayments.length - this.newOrderPayments.length;
                    if (index >= existingOrdersCount) {
                        //the index refers to a new payment, we also have to remove it from the newOrderPayments
                        var newPaymentsIndex = index - existingOrdersCount;
                        this.newOrderPayments.splice(newPaymentsIndex, 1);
                        this.$emit('change', this.newOrderPayments);
                    }
                }
                this.currentOrderPayments.splice(index, 1);
            },

            customiseCell: function (args) {
                if ((args.column.field == 'reference') && (args.name == 'queryCellInfo') && args.data.enablePaymentLink) {
                    args.cell.innerHTML = "<a href='" + this.paymentLinkBaseUrl + args.data.paymentId + "' target='_blank'>" + args.data.reference + "</a>";
                }
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },
        },
        mounted: function () {
            this.loadOrderPayments();
            this.loadGstValues();
        }
    }
</script>